import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  UNSUPPORTED_CARD_BRAND_MESSAGE,
  isUnsupportedCardBrand,
} from 'v3/constants/cardBrands';

type UseUnsupportedCardBrand = {
  unsupportedBrand: string | null;
  checkBrand: (brand?: string) => boolean;
};

/**
 * Flags card brands the account cannot accept, and warns the member once.
 *
 * `CardElement`'s onChange fires on every keystroke and Stripe resolves the
 * brand from the second digit (Amex BINs are 34/37), so warning on each call
 * would stack a dozen toasts while the member is still typing the number.
 * The ref tracks which brand has already been announced; it resets when the
 * brand leaves the unsupported set, so re-entering one warns again.
 */
export const useUnsupportedCardBrand = (): UseUnsupportedCardBrand => {
  const [unsupportedBrand, setUnsupportedBrand] = useState<string | null>(null);
  const warnedBrand = useRef<string | null>(null);

  const checkBrand = useCallback((brand?: string): boolean => {
    if (!isUnsupportedCardBrand(brand)) {
      warnedBrand.current = null;
      setUnsupportedBrand(null);
      return false;
    }

    const currentBrand = brand as string;
    setUnsupportedBrand(currentBrand);

    if (warnedBrand.current !== currentBrand) {
      warnedBrand.current = currentBrand;
      toast.error(UNSUPPORTED_CARD_BRAND_MESSAGE);
    }

    return true;
  }, []);

  return { unsupportedBrand, checkBrand };
};
